import React from 'react';
import {useLocation, useParams} from 'react-router-dom';
import Redirect from './redirect';
import searchFilter from '../utilities/search-filter';
import {ErrorBoundary} from 'react-error-boundary';
import Top from './top';
import boom from './boom';
import {Helmet} from 'react-helmet-async';
import Skeleton from './skeleton/skeleton';
import FallbackComponent from './fallback-component';
import {graphql, useLazyLoadQuery} from 'react-relay/hooks';
import {FormattedMessage} from 'react-intl';
import './service-list.scss';
import getTagValue from '../utilities/get-tag-value';
import getImageUrl from './operator-interface/get-image-url';
import SuspenseImageStaticSize from '../utilities/suspense-image-static-size';
import i18n from '../utilities/i18n';
import useQuery from '../utilities/use-query';
import {RouterLinkButton} from './button/button';

export const MERCHANT_SERVICES = {
    'privatbank': {
        serviceIdList: ['69a746ae36f7fa1fd9e1ce12', '69a7473b36f7fa1fd9e1ce17'],
        support: {
            formatted: '0-800-33-56-89',
            number: '0800335689'
        }
    },
    '616d6faa56ad07763a191c3e': {
        serviceIdList: ['67a4dae53877f1796ef2a1c1'],
        support: {
            formatted: '+38-093-283-58-71',
            number: '+380932835871'
        }
    }
};

const serviceListServiceByIdQuery = graphql`
  query serviceListServiceByIdQuery($id: String, $includeService: Boolean = true) {
    serviceById(id: $id) @include(if: $includeService) {
      id
      name
      nameUk
      details
      tags
    }
  }
`;

const ServiceCard = React.memo(({serviceId}) => {
    const {locale = 'uk'} = useQuery();
    const location = useLocation();
    const {serviceById} = useLazyLoadQuery(
        serviceListServiceByIdQuery,
        {id: serviceId, includeService: !!serviceId}
    );
    const mmLocation = getTagValue(serviceById.tags, 'location', ':');
    const img = getImageUrl(serviceById.details, serviceById.tags);
    return (
        <div className='mm-service-card align-items-stretch display-flex column-gap-1rem'>
            <div className='display-flex flex-direction-column flex-1 mw768-padding-left-1rem mw768-padding-top-0dot875rem mw768-padding-bottom-0dot875rem'>
                <p className='text-lg color-gray-700 semibold'>{i18n(serviceById, 'name', locale)}</p>
                <p className='padding-top-0dot5rem flex-1 text-sm color-gray-500'>{mmLocation}</p>
                <div className='width-fit-content'>
                    <RouterLinkButton {...{color: 'orange', size: 'lg', plain: true, fluid: 'always', to: `/${searchFilter(location.search, {serviceId})}`}}>
                        <FormattedMessage defaultMessage='To micromarket'/>
                    </RouterLinkButton>
                </div>
            </div>
            <div className='display-flex justify-content-center'>
                {img && 
                    <SuspenseImageStaticSize className='mm-service-card__img display-block border-radius-0dot25rem' src={img}/>
                }
            </div>
        </div>
    )
});

const ServiceList = () => {
    const {merchantId} = useParams();
    const location = useLocation();
    const {serviceIdList, support} = MERCHANT_SERVICES[merchantId];
    if (!serviceIdList || serviceIdList.length < 1) {
        return <Redirect {...{to: `/${searchFilter(location.search)}`}} replace/>
    }
    return (
        <>
            <Helmet>
                <title>Time to eat micromarkets list</title>
                <meta name='description' content='Time2eat'/>
            </Helmet>
            <div className='mm-service-list'>
                <ErrorBoundary {...{FallbackComponent: boom}}>
                    <div className='mw768-max-width-80rem mw768-margin-0-auto'>
                        <Top {...{customDefaultSupport: support}}/>
                        <h1 className='padding-top-1dot5rem mw768-padding-top-1rem color-gray-900 display-xs mw768-display-md semibold padding-left-1dot25rem mw768-padding-left-2rem padding-right-1dot25rem mw768-padding-right-2rem'>
                            <FormattedMessage defaultMessage='Micromarkets'/>
                        </h1>
                        <React.Suspense fallback={
                            <div className='padding-left-1dot25rem mw768-padding-left-2rem padding-right-1dot25rem mw768-padding-right-2rem padding-top-1dot5rem mw768-padding-top-2rem'>
                                <Skeleton/>
                            </div>
                        }>
                            <ErrorBoundary {...{FallbackComponent}}>
                                <div className='padding-top-1dot5rem mw768-padding-top-2rem display-grid grid-cols-1 mw768-grid-cols-2 column-gap-2rem mw768-row-gap-2rem padding-left-1dot25rem mw768-padding-left-2rem padding-right-1dot25rem mw768-padding-right-2rem'>
                                    {serviceIdList.map(serviceId =>
                                        <ServiceCard {...{key: serviceId, serviceId}}/>
                                    )}
                                </div>
                            </ErrorBoundary>
                        </React.Suspense>
                    </div>
                </ErrorBoundary>
            </div>
        </>
    );
};

export default React.memo(ServiceList);